import type { CSSProperties } from "react";

export type ShowcaseGridSpan = 1 | 2 | 3;

/** Matches `masonry.css` narrow-phone breakpoint (3-column catalog grid). */
export const CATALOG_GRID_MOBILE_MEDIA = "(max-width: 480px)" as const;

/** Maximum tile span on the narrow-phone catalog grid. */
export const CATALOG_GRID_MOBILE_MAX_SPAN = 2 as const;

export interface ShowcaseGridDimensions {
  cols: ShowcaseGridSpan;
  rows: ShowcaseGridSpan;
}

/** @deprecated Use {@link ShowcaseGridDimensions} via gridCols/gridRows. */
export type ShowcaseGridSize = ShowcaseGridSpan;

export function coerceShowcaseGridSpan(value: number | null | undefined): ShowcaseGridSpan {
  if (value === 2 || value === 3) {
    return value;
  }
  return 1;
}

export function readShowcaseGridDimensions(row: {
  grid_cols?: number | null;
  grid_rows?: number | null;
  size?: number | null;
}): ShowcaseGridDimensions {
  if (row.grid_cols != null && row.grid_rows != null) {
    return {
      cols: coerceShowcaseGridSpan(row.grid_cols),
      rows: coerceShowcaseGridSpan(row.grid_rows),
    };
  }

  const square = coerceShowcaseGridSpan(row.size);
  return { cols: square, rows: square };
}

export function dimensionsEqual(a: ShowcaseGridDimensions, b: ShowcaseGridDimensions): boolean {
  return a.cols === b.cols && a.rows === b.rows;
}

export function squareDimensions(span: ShowcaseGridSpan): ShowcaseGridDimensions {
  return { cols: span, rows: span };
}

/** @deprecated Use {@link squareDimensions}. */
export function coerceShowcaseGridSize(size: number | null | undefined): ShowcaseGridSpan {
  return coerceShowcaseGridSpan(size);
}

export function dimensionsFromCorner(
  row: number,
  col: number,
  squareOnly: boolean
): ShowcaseGridDimensions {
  const cols = Math.min(col + 1, 3) as ShowcaseGridSpan;
  const rows = Math.min(row + 1, 3) as ShowcaseGridSpan;
  if (!squareOnly) {
    return { cols, rows };
  }

  const span = Math.max(cols, rows) as ShowcaseGridSpan;
  return squareDimensions(span);
}

export function dimensionsAriaLabel(dims: ShowcaseGridDimensions): string {
  return `${dims.cols} by ${dims.rows} tile size`;
}

export function clampShowcaseGridSpan(
  span: ShowcaseGridSpan,
  maxSpan: number = CATALOG_GRID_MOBILE_MAX_SPAN
): ShowcaseGridSpan {
  return Math.min(span, maxSpan) as ShowcaseGridSpan;
}

export function clampShowcaseGridDimensions(
  dims: ShowcaseGridDimensions,
  maxSpan: number = CATALOG_GRID_MOBILE_MAX_SPAN
): ShowcaseGridDimensions {
  return {
    cols: clampShowcaseGridSpan(dims.cols, maxSpan),
    rows: clampShowcaseGridSpan(dims.rows, maxSpan),
  };
}

export function showcaseGridItemStyle(dims: ShowcaseGridDimensions): CSSProperties {
  const maxSpan = Math.max(dims.cols, dims.rows);
  return {
    "--grid-col-span": dims.cols,
    "--grid-row-span": dims.rows,
    gridColumn: `span ${dims.cols}`,
    gridRow: `span ${dims.rows}`,
    aspectRatio: `${dims.cols} / ${dims.rows}`,
    ...(maxSpan > 1 ? { zIndex: maxSpan - 1 } : {}),
  } as CSSProperties;
}

export function legacySizeFromDimensions(dims: ShowcaseGridDimensions): ShowcaseGridSpan {
  return Math.max(dims.cols, dims.rows) as ShowcaseGridSpan;
}

export function isCellFilled(row: number, col: number, dims: ShowcaseGridDimensions): boolean {
  return row < dims.rows && col < dims.cols;
}
